// @core/stores/affiliate/AffiliatePostbackStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { affiliateApi } from '@core/api';
import type {
  CreatePostbackRequest,
  Postback,
  UpdatePostbackRequest,
} from '@core/types/affiliate';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class AffiliatePostbackStore {
  // List state
  items: Postback[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Postback | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Postback>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Postback | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Postback | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create postback for offer
   */
  async CreatePostback(offer_id: string, data: CreatePostbackRequest): Promise<Postback> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await affiliateApi.CreatePostback(offer_id, data);
      const item = response.postback;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete postback
   */
  async DeletePostback(postback_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await affiliateApi.DeletePostback(postback_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== postback_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === postback_id) {
          this.currentItem = null;
        }

        this.removeFromCache(postback_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * List postbacks for offer
   */
  async ListPostbacks(offer_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await affiliateApi.ListPostbacks(offer_id, params);

      runInAction(() => {
        this.items = response.items ?? [];
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update postback
   */
  async UpdatePostback(postback_id: string, data: UpdatePostbackRequest): Promise<Postback> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await affiliateApi.UpdatePostback(postback_id, data);
      const item = response.postback;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const affiliatePostbackStore = new AffiliatePostbackStore();
